import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { userService, filialeService, parametresService } from '../services/api';
import api from '../services/api';
import { LABELS_ROLES } from '../constants/roles';
import useEstMobile from '../hooks/useEstMobile';
import MenuProfil from '../components/MenuProfil';
import {
  IconeCloche, IconeArchive, IconeDocument, IconeValide, IconeEnregistrer, IconeDossier,
  IconeRefuse, IconeReglages, IconeCle, IconeHorloge, IconeUtilisateurs,
  IconeBatiment, IconeCarte, IconePanier, IconeCompte, IconeRafraichir,
  IconeAppareilPhoto, IconeTrombone, IconeCrayon, IconeFermer, IconeGraphique,} from '../components/IconesSVG';

const ETAPES_DEFAUT = [
  { code: 'controle_terrain', role: 'acheteur', label: 'Validation Acheteur', ordre: 1, action: 'approuver', statut: 'en_controle_terrain', geler_apres: false, rejet: { etape: 'demandeur', statut: 'en_correction' }, upload_categorie: null },
  { code: 'memo_filiale', role: 'preparateur_memo_filiale', label: 'Memo Filiale', ordre: 2, action: 'upload_transmettre', statut: 'en_memo_filiale', geler_apres: false, rejet: { etape: 'demandeur', statut: 'en_correction' }, upload_categorie: 'memo_filiale' },
  { code: 'signature_dg', role: 'dg_filiale', label: 'Signature DG Filiale', ordre: 3, action: 'signer', statut: 'en_signature_dg_filiale', geler_apres: true, rejet: { etape: 'memo_filiale', statut: 'en_correction_memo' }, upload_categorie: null },
  { code: 'holding', role: 'preparateur_memo_holding', label: 'Memo Holding', ordre: 4, action: 'upload_transmettre', statut: 'envoye_holding', geler_apres: false, rejet: { etape: 'demandeur', statut: 'en_correction' }, upload_categorie: 'memo_holding' },
  { code: 'validation_assistante', role: 'assistante_pdg', label: 'Validation Assistante PDG', ordre: 5, action: 'approuver', statut: 'en_validation_assistante_pdg', geler_apres: false, rejet: { etape: 'holding', statut: 'en_correction_pdg' }, upload_categorie: null },
  { code: 'parapheur_pdg', role: 'pdg', label: 'Signature PDG', ordre: 6, action: 'signer_scellage', statut: 'dans_parapheur_pdg', geler_apres: true, rejet: { etape: 'validation_assistante', statut: 'en_correction_pdg' }, upload_categorie: null },
  { code: 'archive', role: null, label: 'Archive', ordre: 7, action: null, statut: 'solde', geler_apres: false, rejet: null, upload_categorie: null },
];

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const estMobile = useEstMobile();
  const [tab, setTab] = useState('Utilisateurs');
  const [filiales, setFiliales] = useState([]);
  const [users, setUsers] = useState([]);
  const [params, setParams] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Modal utilisateur
  const [showUserModal, setShowUserModal] = useState(false);
  const [uploadingSignature, setUploadingSignature] = React.useState(false);
  const inputSignatureRef = React.useRef(null);
  const [userEdit, setUserEdit] = useState(null);
  // Mot de passe temporaire affiche une seule fois apres reinitialisation
  const [mdpTemporaire, setMdpTemporaire] = useState(null);
  const [userForm, setUserForm] = useState({ identifiant: '', prenom: '', nom: '', email: '', telephone: '', role: 'demandeur', filiale_id: '', province: '', motDePasse: '', is_global: false, actif: true });

  // Modal filiale
  const [showFilialeModal, setShowFilialeModal] = useState(false);
  const [filialeEdit, setFilialeEdit] = useState(null);
  const [filialeForm, setFilialeForm] = useState({ code: '', nom: '', email: '', telephone: '', couleur: '#2980B9', seuil_pdg: 5000000 });

  // Modal workflow
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [filialeSelectionnee, setFilialeSelectionnee] = useState(null);
  const [workflowEdit, setWorkflowEdit] = useState([]);
  const [modeleMemoFiliale, setModeleMemoFiliale] = useState(null);

  useEffect(() => { chargerDonnees(); }, []);

  const chargerDonnees = async () => {
    setLoading(true);
    try {
      const [usersRes, filialesRes, paramsRes] = await Promise.all([
        userService.getAll(),
        filialeService.getAll(),
        parametresService.get().catch(() => ({ data: null })),
      ]);
      setUsers(usersRes.data || []);
      setFiliales(filialesRes.data || []);
      setParams(paramsRes.data || null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  // ===== UTILISATEURS =====
  const genererIdentifiant = async (filialeId) => {
    if (!filialeId) return;
    const filiale = filiales.find(f => (f._id || f.id) === filialeId);
    if (!filiale) return;
    try {
      const res = await api.get('/users/next-identifiant?filiale_code=' + filiale.code);
      setUserForm(prev => ({ ...prev, identifiant: res.identifiant || '' }));
    } catch (e) { console.error('Erreur generation identifiant:', e); }
  };

  const reinitialiserMotDePasse = async (u) => {
    const nom = (u.prenom || '') + ' ' + (u.nom || '');
    if (!window.confirm(
      `Réinitialiser le mot de passe de ${nom.trim() || u.identifiant} ?\n\n`
      + 'Un mot de passe temporaire sera généré. L\'utilisateur devra le changer '
      + 'à sa prochaine connexion et ne pourra rien faire d\'autre avant.'
    )) return;
    try {
      const res = await api.post(`/users/${u._id || u.id}/reset-password`);
      // Le temporaire n'est renvoye qu'ici : il n'est plus jamais consultable.
      setMdpTemporaire({ identifiant: res.data?.identifiant || u.identifiant,
                         motDePasse: res.data?.mot_de_passe_temporaire });
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Échec de la réinitialisation');
    }
  };

  const ouvrirCreerUser = () => {
    setUserEdit(null);
    setUserForm({ identifiant: '', prenom: '', nom: '', email: '', telephone: '', role: 'demandeur', filiale_id: '', province: '', motDePasse: '', is_global: false, actif: true });
    setShowUserModal(true);
  };
  const ouvrirEditerUser = (u) => {
    setUserEdit(u);
    setUserForm({ identifiant: u.identifiant || '', prenom: u.prenom || '', nom: u.nom || '', email: u.email || '', telephone: u.telephone || '', role: u.role || 'demandeur', filiale_id: u.filiale_id || u.filiale?._id || u.filiale?.id || '', province: u.province || '', motDePasse: '', is_global: u.is_global || false, actif: u.actif !== false });
    setShowUserModal(true);
  };

  const uploadSignature = async (userId, file) => {
    if (!file || !userId) return;
    setUploadingSignature(true);
    try {
      const formData = new FormData();
      formData.append('signature', file);
      await api.post('/users/' + userId + '/signature', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await chargerDonnees();
      alert('Signature uploadée avec succès');
    } catch(e) {
      alert('Erreur upload signature: ' + (e.response?.data?.message || e.message));
    } finally {
      setUploadingSignature(false);
    }
  };

  const sauvegarderUser = async () => {
    if (userEdit === null && userForm.filiale_id === '') {
      setError('Veuillez selectionner une filiale pour generer l identifiant');
      return;
    }
    setSaving(true); setError('');
    try {
      if (userEdit) {
        const updates = { ...userForm };
        if (!updates.motDePasse) delete updates.motDePasse;
        await userService.update(userEdit._id || userEdit.id, updates);
      } else {
        await userService.create(userForm);
      }
      setShowUserModal(false);
      chargerDonnees();
    } catch (err) { setError(err.response?.data?.message || err.message); }
    finally { setSaving(false); }
  };
  const toggleActifUser = async (u) => {
    const action = u.actif !== false ? 'désactiver' : 'réactiver';
    if (!window.confirm('Voulez-vous ' + action + ' cet utilisateur ?')) return;
    try {
      const id = u._id || u.id;
      if (u.actif !== false) {
        await userService.delete(id);
      } else {
        await api.patch('/users/' + id + '/actif', { actif: true });
      }
      chargerDonnees();
    } catch (err) { alert(err.message); }
  };

  // ===== FILIALES =====
  const ouvrirCreerFiliale = () => {
    setFilialeEdit(null);
    setFilialeForm({ code: '', nom: '', email: '', telephone: '', couleur: '#2980B9' });
    setShowFilialeModal(true);
  };
  const ouvrirEditerFiliale = (f) => {
    setFilialeEdit(f);
    setFilialeForm({ code: f.code || '', nom: f.nom || '', email: f.email || '', telephone: f.telephone || '', couleur: f.couleur || '#2980B9', seuil_pdg: f.seuil_pdg ?? 5000000 });
    setShowFilialeModal(true);
  };
  const sauvegarderFiliale = async () => {
    setSaving(true); setError('');
    try {
      if (filialeEdit) {
        const id = filialeEdit._id || filialeEdit.id;
        const { seuil_pdg: seuil, ...champs } = filialeForm;
        await filialeService.update(id, champs);
        // Le seuil a sa propre route : il decide quels dossiers echappent au PDG,
        // et reste donc reserve a l'administration.
        if (String(seuil) !== String(filialeEdit.seuil_pdg)) {
          await filialeService.definirSeuil(id, Number(seuil));
        }
      }
      else await filialeService.create(filialeForm);
      setShowFilialeModal(false);
      chargerDonnees();
    } catch (err) { setError(err.response?.data?.message || err.message); }
    finally { setSaving(false); }
  };
  const toggleFilialeActif = async (filiale) => {
    try { await filialeService.update(filiale._id || filiale.id, { active: !filiale.active }); chargerDonnees(); }
    catch (err) { console.error(err); }
  };

  // ===== WORKFLOW =====
  const ouvrirWorkflow = async (filiale) => {
    setFilialeSelectionnee(filiale);
    const etapes = filiale.workflow?.etapes?.length > 0 ? filiale.workflow.etapes : ETAPES_DEFAUT;
    setWorkflowEdit(JSON.parse(JSON.stringify(etapes)));
    setShowWorkflowModal(true);
    setModeleMemoFiliale(null);
    try {
      const filialeId = filiale._id || filiale.id;
      const res = await api.get('/demandes/modele-memo/filiale?filiale_id=' + filialeId);
      setModeleMemoFiliale(res.data || null);
    } catch (err) { console.error('Aucun gabarit memo:', err.message); }
  };
  const sauvegarderWorkflow = async () => {
    setSaving(true); setError('');
    try {
      await filialeService.update(filialeSelectionnee._id || filialeSelectionnee.id, { workflow: { etapes: workflowEdit } });
      setShowWorkflowModal(false);
      chargerDonnees();
    } catch (err) { setError(err.response?.data?.message || err.message); }
    finally { setSaving(false); }
  };

  const usersFiltres = users.filter(u => {
    const q = search.toLowerCase();
    return (u.identifiant || '').toLowerCase().includes(q) || (u.nom || '').toLowerCase().includes(q) || (u.prenom || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const TABS = ['Utilisateurs', 'Filiales', 'Zones & Responsables', 'Parametres GED'];
  const ROLES_OPTIONS = ['demandeur', 'acheteur', 'direction_achats', 'preparateur_memo_filiale', 'dg_filiale', 'preparateur_memo_holding', 'assistante_pdg', 'pdg', 'responsable', 'responsable_service', 'admin'];

  return (
    <div style={s.app}>
      {/* SIDEBAR */}
      <div style={s.sidebar}>
        <div style={s.sidebarTop}>
          <div style={s.logoText}>JETA GED</div>
        </div>
        <div style={s.nav}>
          {[
            { label: 'A traiter', icon: IconeCloche, path: '/parapheur' },
            { label: 'Tous les docs', icon: IconeArchive, path: '/dashboard' },
            { label: 'Mes documents', icon: IconeDocument, path: '/dashboard' },
            { label: 'Approuves', icon: IconeValide, path: '/dashboard' },
            { label: 'A regulariser', icon: IconeEnregistrer, path: '/dashboard' },
            { label: 'Plan classement', icon: IconeDossier, path: '/dashboard' },
            { label: 'Rejetes', icon: IconeRefuse, path: '/dashboard' },
            { label: 'Archives', icon: IconeArchive, path: '/archives' },
            { label: 'Analyses', icon: IconeGraphique, path: '/analyses' },
          ].map(item => (
            <div key={item.label} onClick={() => navigate(item.path)} style={s.navItem}>
              <span style={{ display: 'inline-flex' }}><item.icon taille={16} /></span><span style={s.navLabel}>{item.label}</span>
            </div>
          ))}
          <div style={{ ...s.navItem, ...s.navActive }}>
            <span style={{ display: 'inline-flex' }}><IconeReglages taille={16} /></span><span style={s.navLabel}>Administration</span>
          </div>
        </div>
        <div style={s.sidebarBottom}>
          <MenuProfil sens="haut" couleurAvatar="#C0392B" libelle={(user?.prenom || '') + ' ' + (user?.nom || '')} />
        </div>
      </div>

      {/* MAIN */}
      <div style={s.main}>
        <div style={s.topbar}>
          <div>
            <div style={s.pageTitle}>Administration</div>
            <div style={s.pageSub}>Utilisateurs · Filiales · Parametres GED</div>
          </div>
          {tab === 'Utilisateurs' && <button onClick={ouvrirCreerUser} style={s.btnNew}>+ Nouvel utilisateur</button>}
          {tab === 'Filiales' && <button onClick={ouvrirCreerFiliale} style={s.btnNew}>+ Nouvelle filiale</button>}
        </div>

        <div style={s.body}>
          {/* TABS */}
          <div style={s.tabs}>
            {TABS.map(t => (
              <button key={t} onClick={() => setTab(t)}
                style={{ ...s.tab, ...(tab === t ? s.tabActive : {}) }}>
                {t === 'Utilisateurs' ? <IconeUtilisateurs taille={14} /> : t === 'Filiales' ? <IconeBatiment taille={14} /> : t === 'Zones & Responsables' ? <IconeCarte taille={14} /> : <IconeReglages taille={14} />} {t}
                {t === 'Utilisateurs' && <span style={{ ...s.tabBadge, ...(tab === t ? s.tabBadgeActive : {}) }}>{users.length}</span>}
                {t === 'Filiales' && <span style={{ ...s.tabBadge, ...(tab === t ? s.tabBadgeActive : {}) }}>{filiales.length}</span>}
              </button>
            ))}
          </div>

          {error && <div style={s.errorMsg}>{error}</div>}

          {loading ? (
            <div style={s.empty}><IconeHorloge taille={14} /> Chargement...</div>
          ) : tab === 'Utilisateurs' ? (
            <>
              <input placeholder="Rechercher un utilisateur..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...s.searchInput, marginBottom: 14 }} />
              {estMobile ? (
                /* A 375 px, sept colonnes sont illisibles : une carte par compte,
                   chaque ligne d'action gardant une cible tactile suffisante. */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {usersFiltres.map(u => (
                    <div key={u._id || u.id} style={{ background: '#fff', border: '0.5px solid #e5e7eb', borderRadius: 12, padding: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <div style={s.avatarSm}>{u.prenom?.[0]}{u.nom?.[0]}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="tronque" style={s.userName2}>{u.prenom} {u.nom}</div>
                          <div className="tronque" style={s.userPhone}>{u.email || u.identifiant}</div>
                        </div>
                        <span style={{ ...s.statusDot, background: u.actif !== false ? '#27AE60' : '#C0392B', flexShrink: 0 }}>
                          {u.actif !== false ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                        <span style={s.rolePill}>{LABELS_ROLES[u.role] || u.role}</span>
                        <span style={{ ...s.rolePill, background: '#F5F6FA', color: '#555' }}>{u.filiale?.code || u.filiale_code || '—'}</span>
                        {u.is_global && <span style={s.globalBadge}>Global</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => ouvrirEditerUser(u)} style={{ ...s.actionBtn, flex: 1, minHeight: 44 }}>Modifier</button>
                        <button onClick={() => reinitialiserMotDePasse(u)} style={{ ...s.actionBtn, flex: 1, minHeight: 44 }}>Mot de passe</button>
                        <button onClick={() => toggleActifUser(u)}
                          style={{ ...s.actionBtn, flex: 1, minHeight: 44, color: u.actif !== false ? '#C0392B' : '#27AE60' }}>
                          {u.actif !== false ? 'Désactiver' : 'Activer'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <div style={s.tableWrap}>
                <table className="table-mobile" style={s.table}>
                  <thead>
                    <tr>
                      {['Utilisateur', 'Identifiant', 'Role', 'Filiale', 'Global', 'Statut', 'Actions'].map(h => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {usersFiltres.map(u => (
                      <tr key={u._id || u.id} style={s.tr}>
                        <td style={s.td}>
                          <div style={s.userRow}>
                            <div style={s.avatarSm}>{u.prenom?.[0]}{u.nom?.[0]}</div>
                            <div>
                              <div style={s.userName2}>{u.prenom} {u.nom}</div>
                              <div style={s.userPhone}>{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={s.td}><code style={{ fontSize: 12, background: '#F5F6FA', padding: '2px 6px', borderRadius: 4 }}>{u.identifiant}</code></td>
                        <td style={s.td}><span style={s.rolePill}>{LABELS_ROLES[u.role] || u.role}</span></td>
                        <td style={s.td}>{u.filiale?.code || u.filiale_code || <span style={s.noGlobal}>—</span>}</td>
                        <td style={s.td}>{u.is_global ? <span style={s.globalBadge}>Global</span> : <span style={s.noGlobal}>—</span>}</td>
                        <td style={s.td}><span style={{ ...s.statusDot, background: u.actif !== false ? '#27AE60' : '#C0392B' }}>{u.actif !== false ? 'Actif' : 'Inactif'}</span></td>
                        <td style={s.td}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => ouvrirEditerUser(u)} style={s.actionBtn} title="Modifier"><IconeCrayon taille={14} /></button>
                            <button onClick={() => reinitialiserMotDePasse(u)} style={s.actionBtn}
                              title="Réinitialiser le mot de passe"><IconeCle taille={16} /></button>
                            <button onClick={() => toggleActifUser(u)}
                              style={{ ...s.actionBtn, color: u.actif !== false ? '#C0392B' : '#27AE60' }}
                              title={u.actif !== false ? 'Désactiver' : 'Réactiver'}>
                              {u.actif !== false ? 'Désactiver' : 'Activer'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </>
          ) : tab === 'Filiales' ? (
            <div style={s.filialesGrid}>
              {filiales.map(f => (
                <div key={f._id || f.id} style={s.filialeCard}>
                  <div style={s.filialeHeader}>
                    <span style={s.filialeCode}>{f.code}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: f.active ? '#EAFAF1' : '#F2F3F4', color: f.active ? '#1E8449' : '#888', fontWeight: 500 }}>
                      {f.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div style={s.filialeNom}>{f.nom}</div>
                  {f.logo && (
                    <div style={{ marginBottom: 12 }}>
                      <img src={(process.env.REACT_APP_STATIC_URL || 'http://localhost:5001') + '/' + f.logo} alt={f.code} style={{ height: 48, objectFit: 'contain' }} />
                    </div>
                  )}
                  <div style={s.workflowPreview}>
                    <div style={s.workflowTitle}>Etapes actives</div>
                    {(f.workflow?.etapes || ETAPES_DEFAUT).filter(e => e.role).slice(0, 4).map(e => (
                      <div key={e.code} style={s.workflowStep}>
                        <div style={{ ...s.workflowDot, background: '#27AE60' }} />
                        <span style={s.workflowStepLabel}>{e.label}</span>
                        {e.multi && <span style={s.multiTag}>multi</span>}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => ouvrirEditerFiliale(f)} style={s.btnWorkflow}><IconeCrayon taille={13} /> Modifier</button>
                    <button onClick={() => ouvrirWorkflow(f)} style={s.btnWorkflow}><IconeReglages taille={13} /> Workflow</button>
                    <button onClick={() => toggleFilialeActif(f)} style={{ ...s.btnWorkflow, color: f.active ? '#C0392B' : '#27AE60' }}>
                      {f.active ? 'Désactiver' : 'Activer'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

          ) : tab === 'Zones & Responsables' ? (
            <div style={{ padding: '0 4px' }}>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
                Vue des affectations par province — les acheteurs et responsables couvrent chacun une province gabonaise.
                Pour modifier la province d'un utilisateur, utilisez le bouton de modification dans l'onglet Utilisateurs.
              </div>

              {/* Grouper par filiale */}
              {filiales.map(filiale => {
                const filialeId = filiale._id || filiale.id;
                const usersFiliale = users.filter(u =>
                  (u.filiale_id === filialeId || u.filiale?.id === filialeId || u.filiale?._id === filialeId) &&
                  (u.role === 'acheteur' || u.role === 'responsable') &&
                  u.actif !== false
                );
                if (usersFiliale.length === 0) return null;

                return (
                  <div key={filialeId} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                      {filiale.logo && <img src={(process.env.REACT_APP_STATIC_URL || 'http://localhost:5001') + '/' + filiale.logo} alt={filiale.code} style={{ height: 32, objectFit: 'contain' }} />}
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0D1B2A' }}>{filiale.nom}</div>
                        <div style={{ fontSize: 11, color: '#888' }}>{filiale.code} · {usersFiliale.length} acteur(s) configure(s)</div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                      {usersFiliale.map(u => (
                        <div key={u._id || u.id} style={{ background: '#F9FAFB', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: u.role === 'acheteur' ? '#C0392B' : '#27AE60', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                              {u.prenom?.[0]}{u.nom?.[0]}
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#0D1B2A' }}>{u.prenom} {u.nom}</div>
                              <div style={{ fontSize: 10, color: '#888' }}>{u.identifiant}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: u.role === 'acheteur' ? '#FDEDEC' : '#EAFAF1', color: u.role === 'acheteur' ? '#C0392B' : '#1E8449', fontWeight: 600 }}>
                              {u.role === 'acheteur' ? <><IconePanier taille={12} /> Acheteur</> : <><IconeCompte taille={12} /> Responsable</>}
                            </span>
                            {u.province ? (
                              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#EAF4FB', color: '#1A5E8A', fontWeight: 600 }}>
                                <IconeCarte taille={11} /> {u.province}
                              </span>
                            ) : (
                              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#F2F3F4', color: '#888' }}>
                                Toutes provinces
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {users.filter(u => (u.role === 'acheteur' || u.role === 'responsable') && u.actif !== false).length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#888', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
                  Aucun acheteur ou responsable configuré
                </div>
              )}
            </div>

) : tab === 'Parametres GED' && params ? (
            <div style={{ background: '#fff', borderRadius: 12, border: '0.5px solid #e5e7eb', padding: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: '#0D1B2A', marginBottom: 20 }}><IconeReglages taille={15} /> Parametres d archivage automatique</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[
                  { key: 'delai_archivage_jours', label: 'Délai archivage (jours)', type: 'number' },
                  { key: 'rappel_avant_archivage', label: 'Rappel avant archivage (jours)', type: 'number' },
                ].map(p => (
                  <div key={p.key} style={s.formGroup}>
                    <label style={s.label}>{p.label}</label>
                    <input type={p.type} style={s.input} value={params[p.key] || ''} onChange={e => setParams({ ...params, [p.key]: e.target.value })} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={async () => { setSaving(true); try { await parametresService.update(params); alert('Sauvegardé !'); } catch (e) { alert(e.message); } setSaving(false); }} style={s.btnSave} disabled={saving}>
                  {saving ? <><IconeHorloge taille={13} />...</> : <><IconeEnregistrer taille={13} /> Sauvegarder</>}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* MODAL UTILISATEUR */}
      {/* Mot de passe temporaire : affiche une seule fois, jamais reconsultable */}
      {mdpTemporaire && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div className="modale-mobile" style={{ background: '#fff', borderRadius: 14, padding: 26, width: 420, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0D1B2A', marginBottom: 6 }}><IconeCle taille={15} /> Mot de passe réinitialisé</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>
              Compte <strong>{mdpTemporaire.identifiant}</strong>
            </div>
            <div style={{ background: '#F4F6F8', border: '1px dashed #aaa', borderRadius: 8, padding: '14px 16px', textAlign: 'center', marginBottom: 14 }}>
              <div style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, letterSpacing: '0.08em', color: '#0D1B2A', userSelect: 'all' }}>
                {mdpTemporaire.motDePasse}
              </div>
            </div>
            <div style={{ background: '#FEF9E7', border: '1px solid #F1C40F', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#9A7D0A', marginBottom: 18, lineHeight: 1.5 }}>
              Notez-le maintenant : il ne sera plus affiché. L'utilisateur devra le
              changer à sa prochaine connexion.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button"
                onClick={() => navigator.clipboard?.writeText(mdpTemporaire.motDePasse)}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                Copier
              </button>
              <button type="button" onClick={() => setMdpTemporaire(null)}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: '#0D1B2A', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                J'ai noté
              </button>
            </div>
          </div>
        </div>
      )}

      {showUserModal && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setShowUserModal(false)}>
          <div className="modale-mobile" style={s.modal}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>{userEdit ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</div>
              <button onClick={() => setShowUserModal(false)} style={s.closeBtn}><IconeFermer taille={15} /></button>
            </div>
            {error && <div style={s.errorMsg}>{error}</div>}
            <div className="grille-mobile-1" style={s.formGrid}>
              {[
                { key: 'prenom', label: 'Prénom', type: 'text' },
                { key: 'nom', label: 'Nom', type: 'text' },
                { key: 'email', label: 'Email', type: 'email' },
                { key: 'telephone', label: 'Téléphone', type: 'text' },
              ].map(f => (
                <div key={f.key} style={s.formGroup}>
                  <label style={s.label}>{f.label}</label>
                  <input type={f.type} style={s.input} value={userForm[f.key] || ''} onChange={e => setUserForm({ ...userForm, [f.key]: e.target.value })} />
                </div>
              ))}
              {/* Filiale - genere l identifiant automatiquement */}
              <div style={s.formGroup}>
                <label style={s.label}>Filiale <span style={{ color: '#C0392B' }}>*</span></label>
                <select style={s.input} value={userForm.filiale_id} onChange={e => {
                  const fId = e.target.value;
                  setUserForm(prev => ({ ...prev, filiale_id: fId }));
                  if (userEdit === null) genererIdentifiant(fId);
                }}>
                  <option value=''>— Aucune (global) —</option>
                  {filiales.map(f => <option key={f._id || f.id} value={f._id || f.id}>{f.code} — {f.nom}</option>)}
                </select>
              </div>
              {/* Identifiant genere auto apres filiale */}
              <div style={s.formGroup}>
                <label style={s.label}>Identifiant {!userEdit && <span style={{ color: '#27AE60', fontSize: 10 }}>(généré automatiquement)</span>}</label>
                <input
                  type="text"
                  style={{ ...s.input, background: '#F0F0F0', color: '#888', cursor: 'not-allowed' }}
                  value={userForm.identifiant || (userEdit ? '' : '— choisissez une filiale —')}
                  readOnly
                />
              </div>

              {/* Mot de passe */}
              <div style={s.formGroup}>
                <label style={s.label}>{userEdit ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe'}</label>
                <input type="password" style={s.input} value={userForm.motDePasse || ''} onChange={e => setUserForm({ ...userForm, motDePasse: e.target.value })} />
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Rôle</label>
                <select style={s.input} value={userForm.role} onChange={e => setUserForm({ ...userForm, role: e.target.value })}>
                  {ROLES_OPTIONS.map(r => <option key={r} value={r}>{LABELS_ROLES[r] || r}</option>)}
                </select>
              </div>

              {/* Province - visible uniquement pour acheteur et responsable */}
              {(userForm.role === 'acheteur' || userForm.role === 'responsable') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Province <span style={{ color: '#2980B9', fontSize: 10 }}>(zone géographique)</span></label>
                  <select style={s.input} value={userForm.province} onChange={e => setUserForm({ ...userForm, province: e.target.value })}>
                    <option value="">-- Toutes les provinces --</option>
                    <option value="Estuaire">Estuaire</option>
                    <option value="Haut-Ogooué">Haut-Ogooué</option>
                    <option value="Moyen-Ogooué">Moyen-Ogooué</option>
                    <option value="Ngounié">Ngounié</option>
                    <option value="Nyanga">Nyanga</option>
                    <option value="Ogooué-Ivindo">Ogooué-Ivindo</option>
                    <option value="Ogooué-Lolo">Ogooué-Lolo</option>
                    <option value="Ogooué-Maritime">Ogooué-Maritime</option>
                    <option value="Woleu-Ntem">Woleu-Ntem</option>
                  </select>
                </div>
              )}

            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={userForm.is_global} onChange={e => setUserForm({ ...userForm, is_global: e.target.checked })} />
                Accès global (toutes filiales)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={userForm.actif} onChange={e => setUserForm({ ...userForm, actif: e.target.checked })} />
                Compte actif
              </label>
            </div>

            {/* SIGNATURE MANUSCRITE - visible uniquement en mode edition */}
            {userEdit && (
              <div style={{ marginTop: 16, padding: '14px 16px', background: '#F9FAFB', borderRadius: 10, border: '0.5px solid #e5e7eb' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#444', marginBottom: 10 }}>Signature manuscrite</div>
                {userEdit.signature_url ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    <img src={(process.env.REACT_APP_STATIC_URL || 'http://localhost:5001') + '/' + userEdit.signature_url}
                      alt='Signature' style={{ height: 60, maxWidth: 200, objectFit: 'contain', border: '0.5px solid #e5e7eb', borderRadius: 6, background: '#fff', padding: 4 }} />
                    <div style={{ fontSize: 11, color: '#27AE60', fontWeight: 600 }}><IconeValide taille={11} /> Signature configurée</div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Aucune signature configurée</div>
                )}
                <input ref={inputSignatureRef} type='file' accept='image/*' style={{ display: 'none' }}
                  onChange={e => e.target.files[0] && uploadSignature(userEdit._id || userEdit.id, e.target.files[0])} />
                <button type='button' onClick={() => inputSignatureRef.current && inputSignatureRef.current.click()} disabled={uploadingSignature}
                  style={{ padding: '6px 14px', borderRadius: 8, border: '0.5px solid #2980B9', background: '#EAF4FB', color: '#2980B9', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>
                  {uploadingSignature ? <><IconeHorloge taille={13} /> Upload...</> : userEdit.signature_url ? <><IconeRafraichir taille={13} /> Changer la signature</> : <><IconeAppareilPhoto taille={13} /> Uploader la signature</>}
                </button>
              </div>
            )}
            <div style={s.modalFooter}>
              <button onClick={() => setShowUserModal(false)} style={s.btnCancel}>Annuler</button>
              <button onClick={sauvegarderUser} style={s.btnSave} disabled={saving}>{saving ? '...' : userEdit ? 'Mettre à jour' : 'Créer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL FILIALE */}
      {showFilialeModal && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setShowFilialeModal(false)}>
          <div className="modale-mobile" style={s.modal}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>{filialeEdit ? 'Modifier la filiale' : 'Nouvelle filiale'}</div>
              <button onClick={() => setShowFilialeModal(false)} style={s.closeBtn}><IconeFermer taille={15} /></button>
            </div>
            {error && <div style={s.errorMsg}>{error}</div>}
            <div className="grille-mobile-1" style={s.formGrid}>
              {[
                { key: 'code', label: 'Code', type: 'text' },
                { key: 'nom', label: 'Nom', type: 'text' },
                { key: 'email', label: 'Email', type: 'email' },
                { key: 'telephone', label: 'Téléphone', type: 'text' },
              ].map(f => (
                <div key={f.key} style={s.formGroup}>
                  <label style={s.label}>{f.label}</label>
                  <input type={f.type} style={s.input} value={filialeForm[f.key] || ''} onChange={e => setFilialeForm({ ...filialeForm, [f.key]: e.target.value })} />
                </div>
              ))}
              <div style={s.formGroup}>
                <label style={s.label}>Seuil pour validation PDG (FCFA)</label>
                <input type="number" min="0" step="100000" style={s.input}
                  value={filialeForm.seuil_pdg ?? ''}
                  onChange={e => setFilialeForm({ ...filialeForm, seuil_pdg: e.target.value })} />
                <div style={s.aide}>
                  Les demandes en dessous de ce montant seront soldées après validation Holding.
                  {Number(filialeForm.seuil_pdg) > 0 && (
                    <strong> Actuellement : {Number(filialeForm.seuil_pdg).toLocaleString('fr-FR')} FCFA.</strong>
                  )}
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Couleur</label>
                <input type="color" style={{ ...s.input, height: 40, padding: 4 }} value={filialeForm.couleur} onChange={e => setFilialeForm({ ...filialeForm, couleur: e.target.value })} />
              </div>
            </div>
            <div style={s.modalFooter}>
              <button onClick={() => setShowFilialeModal(false)} style={s.btnCancel}>Annuler</button>
              <button onClick={sauvegarderFiliale} style={s.btnSave} disabled={saving}>{saving ? '...' : filialeEdit ? 'Mettre à jour' : 'Créer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL WORKFLOW */}
      {showWorkflowModal && filialeSelectionnee && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setShowWorkflowModal(false)}>
          <div className="modale-mobile" style={{ ...s.modal, width: 600 }}>
            <div style={s.modalHeader}>
              <div>
                <div style={s.modalTitle}>Workflow — {filialeSelectionnee.nom}</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Activez ou desactivez les etapes</div>
              </div>
              <button onClick={() => setShowWorkflowModal(false)} style={s.closeBtn}><IconeFermer taille={15} /></button>
            </div>
            {error && <div style={s.errorMsg}>{error}</div>}

            {/* Couleurs memo PDF */}
            {modeleMemoFiliale && (
              <div style={{ marginBottom: 16, padding: '12px 14px', background: '#F9FAFB', borderRadius: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#0D1B2A', marginBottom: 10 }}><IconeDocument taille={13} /> Couleurs du memo PDF</div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Bande titre (MEMO : ...)</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      key={'titre-' + (filialeSelectionnee?._id || filialeSelectionnee?.id) + '-' + (modeleMemoFiliale?.couleur_bande || 'init')}
                      type="color" value={modeleMemoFiliale?.couleur_bande || '#0D1B2A'}
                      onChange={async (e) => {
                        const nouvelleCouleur = e.target.value;
                        setModeleMemoFiliale(prev => ({ ...prev, couleur_bande: nouvelleCouleur }));
                        try { await api.put('/demandes/modele-memo/filiale', { filiale_id: filialeSelectionnee._id || filialeSelectionnee.id, couleur_bande: nouvelleCouleur }); } catch (err) { console.error(err); }
                      }}
                      style={{ width: 40, height: 32, borderRadius: 6, border: '0.5px solid #ddd', cursor: 'pointer', padding: 2 }} />
                    <div style={{ flex: 1, height: 32, borderRadius: 6, background: modeleMemoFiliale?.couleur_bande || '#0D1B2A', display: 'flex', alignItems: 'center', paddingLeft: 12 }}>
                      <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>MEMO : Apercu titre</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Bande societe</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      key={'societe-' + (filialeSelectionnee?._id || filialeSelectionnee?.id) + '-' + (modeleMemoFiliale?.couleur_bande_societe || 'init')}
                      type="color" value={modeleMemoFiliale?.couleur_bande_societe || '#D5D8DC'}
                      onChange={async (e) => {
                        const nouvelleCouleur = e.target.value;
                        setModeleMemoFiliale(prev => ({ ...prev, couleur_bande_societe: nouvelleCouleur }));
                        try { await api.put('/demandes/modele-memo/filiale', { filiale_id: filialeSelectionnee._id || filialeSelectionnee.id, couleur_bande_societe: nouvelleCouleur }); } catch (err) { console.error(err); }
                      }}
                      style={{ width: 40, height: 32, borderRadius: 6, border: '0.5px solid #ddd', cursor: 'pointer', padding: 2 }} />
                    <div style={{ flex: 1, height: 32, borderRadius: 6, background: modeleMemoFiliale?.couleur_bande_societe || '#D5D8DC', display: 'flex', alignItems: 'center', paddingLeft: 12 }}>
                      <span style={{ color: '#000', fontSize: 11, fontWeight: 600 }}>Societe : Apercu</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Logo filiale */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Logo filiale</div>
              {filialeSelectionnee.logo && (
                <img src={(process.env.REACT_APP_STATIC_URL || 'http://localhost:5001') + '/' + filialeSelectionnee.logo} alt="logo" style={{ height: 60, objectFit: 'contain', marginBottom: 8, display: 'block' }} />
              )}
              <label style={s.btnUpload}>
                <IconeTrombone taille={13} /> Changer le logo (JPEG)
                <input type="file" accept="image/jpeg,image/jpg" style={{ display: 'none' }} onChange={async (e) => {
                  if (!e.target.files[0]) return;
                  const fd = new FormData();
                  fd.append('logo', e.target.files[0]);
                  try {
                    await api.post('/filiales/' + (filialeSelectionnee._id || filialeSelectionnee.id) + '/logo', fd);
                    chargerDonnees();
                    const f = filiales.find(f => (f._id || f.id) === (filialeSelectionnee._id || filialeSelectionnee.id));
                    if (f) setFilialeSelectionnee(f);
                  } catch (err) { alert('Erreur upload logo'); }
                }} />
              </label>
            </div>

            {/* Etapes workflow */}
            {workflowEdit.map((etape, idx) => (
              <div key={etape.code} style={s.workflowEditRow}>
                <div style={s.workflowEditLeft}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: etape.role ? '#27AE60' : '#E5E7EB', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{etape.label}</div>
                    <div style={{ fontSize: 11, color: '#888' }}>Ordre {etape.ordre} · {etape.role ? (LABELS_ROLES[etape.role] || etape.role) : 'terminal'}</div>
                  </div>
                  {etape.action === 'upload_transmettre' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!etape.multi} onChange={e => {
                        const copy = [...workflowEdit];
                        copy[idx] = { ...copy[idx], multi: e.target.checked };
                        setWorkflowEdit(copy);
                      }} />
                      Multi
                    </label>
                  )}
                </div>
                {etape.role && (
                  <button onClick={() => {
                    const copy = [...workflowEdit];
                    copy[idx] = { ...copy[idx], role: etape.role ? null : ETAPES_DEFAUT[idx]?.role };
                    setWorkflowEdit(copy);
                  }} style={{ padding: '4px 12px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: '#555' }}>
                    Inactif
                  </button>
                )}
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: etape.role ? '#EAFAF1' : '#F2F3F4', color: etape.role ? '#1E8449' : '#888', fontWeight: 500 }}>
                  {etape.role ? 'Actif' : 'Inactif'}
                </span>
              </div>
            ))}

            <div style={s.modalFooter}>
              <button onClick={() => setShowWorkflowModal(false)} style={s.btnCancel}>Annuler</button>
              <button onClick={sauvegarderWorkflow} style={s.btnSave} disabled={saving}>{saving ? <><IconeHorloge taille={13} />...</> : <><IconeEnregistrer taille={13} /> Sauvegarder</>}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  app: { display: 'flex', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', background: '#F5F6FA' },
  sidebar: { width: 220, background: '#0D1B2A', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarTop: { display: 'flex', alignItems: 'center', gap: 10, padding: '16px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  logoText: { color: '#fff', fontWeight: 500, fontSize: 15 },
  nav: { flex: 1, padding: '10px 0' },
  navItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', color: '#8899AA', borderRadius: 6, margin: '1px 8px', fontSize: 13 },
  navActive: { background: '#C0392B', color: '#fff' },
  navLabel: { fontSize: 13 },
  sidebarBottom: { padding: 10, borderTop: '1px solid rgba(255,255,255,0.08)' },
  userCard: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  avatar: { width: 30, height: 30, borderRadius: '50%', background: '#C0392B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 500, color: '#fff' },
  userName: { fontSize: 12, fontWeight: 500, color: '#fff' },
  userRole: { fontSize: 10, color: '#8899AA' },
  logoutBtn: { width: '100%', padding: 8, borderRadius: 6, border: 'none', background: 'rgba(255,255,255,0.06)', color: '#8899AA', cursor: 'pointer', fontSize: 12, textAlign: 'left', fontFamily: 'inherit' },
  main: { flex: 1, display: 'flex', flexDirection: 'column' },
  topbar: { background: '#fff', borderBottom: '0.5px solid #e5e7eb', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  pageTitle: { fontSize: 18, fontWeight: 500, color: '#0D1B2A' },
  pageSub: { fontSize: 12, color: '#888', marginTop: 2 },
  btnNew: { padding: '10px 18px', borderRadius: 8, border: 'none', background: '#C0392B', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' },
  body: { padding: 24, flex: 1, overflowY: 'auto' },
  tabs: { display: 'flex', gap: 8, marginBottom: 16 },
  tab: { display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  tabActive: { background: '#0D1B2A', color: '#fff', border: '0.5px solid #0D1B2A' },
  tabBadge: { background: '#e5e7eb', color: '#555', fontSize: 11, padding: '1px 7px', borderRadius: 20 },
  tabBadgeActive: { background: 'rgba(255,255,255,0.2)', color: '#fff' },
  searchInput: { width: '100%', padding: '9px 12px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 13, background: '#fff', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
  tableWrap: { background: '#fff', borderRadius: 10, border: '0.5px solid #e5e7eb', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: '#888', background: '#F9FAFB', borderBottom: '0.5px solid #e5e7eb' },
  tr: { borderBottom: '0.5px solid #f0f0f0' },
  td: { padding: '12px 14px', color: '#0D1B2A', verticalAlign: 'middle' },
  userRow: { display: 'flex', alignItems: 'center', gap: 8 },
  avatarSm: { width: 28, height: 28, borderRadius: '50%', background: '#2980B9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 500, color: '#fff', flexShrink: 0 },
  userName2: { fontSize: 13, fontWeight: 500 },
  userPhone: { fontSize: 11, color: '#888' },
  rolePill: { background: '#EAF4FB', color: '#1A5E8A', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500 },
  globalBadge: { background: '#EAFAF1', color: '#1E8449', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 500 },
  noGlobal: { color: '#aaa', fontSize: 12 },
  statusDot: { padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, color: '#fff' },
  actionBtn: { background: 'transparent', border: '0.5px solid #ddd', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' },
  filialesGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 },
  filialeCard: { background: '#fff', borderRadius: 12, border: '0.5px solid #e5e7eb', padding: 18 },
  filialeHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  filialeCode: { background: '#0D1B2A', color: '#fff', padding: '3px 10px', borderRadius: 6, fontSize: 13, fontWeight: 500 },
  filialeNom: { fontSize: 14, fontWeight: 500, color: '#0D1B2A', marginBottom: 12 },
  workflowPreview: { background: '#F9FAFB', borderRadius: 8, padding: '10px 12px', marginBottom: 12 },
  workflowTitle: { fontSize: 11, color: '#888', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' },
  workflowStep: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 },
  workflowDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  workflowStepLabel: { fontSize: 12, color: '#0D1B2A' },
  multiTag: { fontSize: 10, background: '#EAF4FB', color: '#1A5E8A', padding: '1px 6px', borderRadius: 10 },
  btnWorkflow: { padding: '9px', borderRadius: 8, border: '0.5px solid #ddd', background: 'transparent', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, width: 560, maxHeight: '90vh', overflowY: 'auto', padding: 24 },
  modalHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 },
  modalTitle: { fontSize: 16, fontWeight: 500, color: '#0D1B2A' },
  closeBtn: { background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: '#888' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 },
  aide: { fontSize: 11, color: '#888', marginTop: 5, lineHeight: 1.45 },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 12, fontWeight: 500, color: '#666' },
  input: { padding: '9px 12px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#F9FAFB', boxSizing: 'border-box', width: '100%' },
  workflowRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid #f0f0f0' },
  workflowEditRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '0.5px solid #f0f0f0', gap: 12 },
  workflowEditLeft: { display: 'flex', alignItems: 'center', gap: 10, flex: 1 },
  errorMsg: { background: '#FDEDEC', color: '#96281B', padding: '10px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 },
  modalFooter: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 },
  btnCancel: { padding: '10px 18px', borderRadius: 8, border: '0.5px solid #ddd', background: 'transparent', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  btnSave: { padding: '10px 18px', borderRadius: 8, border: 'none', background: '#C0392B', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' },
  btnUpload: { display: 'inline-block', padding: '8px 16px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
  empty: { textAlign: 'center', padding: 60, color: '#888' },
};
